import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Building2, TrendingUp, TrendingDown, AlertTriangle, 
  DollarSign, Users, Calendar, Target, ArrowRight, 
  BarChart3, PieChart, Activity, Clock, CheckCircle,
  XCircle, AlertCircle, Brain, LineChart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostControlHome = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchProjectData = async (projectId) => {
    setLoading(true);
    try {
      const [projectRes, kpisRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/kpis`)
      ]);
      setProjectData(projectRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Error fetching project data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'upcoming': return 'text-gray-600 bg-gray-100';
      case 'delayed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'border-red-500 bg-red-50';
      case 'medium': return 'border-amber-500 bg-amber-50';
      case 'low': return 'border-green-500 bg-green-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  if (loading && !projectData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="loading-spinner">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-red-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="cost-control-home">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <img 
              src="https://logos-world.net/wp-content/uploads/2020/09/Oracle-Logo.png" 
              alt="Oracle" 
              className="h-6 w-auto"
            />
            <div>
              <h1 className="text-lg font-bold text-gray-900" data-testid="page-title">
                {t('home.title')}
              </h1>
              <p className="text-xs text-gray-500">
                {new Date().toLocaleDateString(i18n.language === 'tr' ? 'tr-TR' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
        
        {/* Project Selector */}
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full" data-testid="project-selector">
            <SelectValue placeholder={t('home.projectSelector')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.project_id} value={project.project_id}>
                {project.project_code} - {project.project_name.substring(0, 30)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Project Info Card */}
        {projectData && (
          <Card className="bg-gradient-to-r from-red-600 to-red-700 text-white">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs opacity-80">{projectData.project_code}</p>
                  <h2 className="text-sm font-bold leading-tight">{projectData.project_name}</h2>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  projectData.status === 'active' ? 'bg-green-500' : 'bg-gray-500'
                }`}>
                  {t('common.active')}
                </span>
              </div>
              <p className="text-xs opacity-80 mb-3">{projectData.client}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs opacity-70">{t('home.contractValue')}</p>
                  <p className="text-lg font-bold">{formatCurrency(kpis?.contract_value || 0)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-70">{t('home.duration')}</p>
                  <p className="text-xs font-medium">
                    {new Date(projectData.start_date).toLocaleDateString('en-US', {month: 'short', year: '2-digit'})} → {new Date(projectData.end_date).toLocaleDateString('en-US', {month: 'short', year: '2-digit'})}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards */}
        {kpis && (
          <div className="grid grid-cols-2 gap-3">
            {/* Progress Card */}
            <Card data-testid="kpi-progress">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <Activity size={18} className="text-blue-600" />
                  <span className={`text-xs font-semibold ${kpis.progress_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {kpis.progress_variance >= 0 ? '+' : ''}{kpis.progress_variance}%
                  </span>
                </div>
                <p className="text-xs text-gray-500">{t('home.physicalProgress')}</p>
                <div className="flex items-baseline gap-1">
                  <p className="text-xl font-bold text-gray-900">{kpis.overall_progress_actual}%</p>
                  <p className="text-xs text-gray-400">/ {kpis.overall_progress_planned}%</p>
                </div>
                <div className="w-full h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all"
                    style={{ width: `${kpis.overall_progress_actual}%` }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Budget Card */}
            <Card data-testid="kpi-budget">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <DollarSign size={18} className="text-green-600" />
                  <span className={`text-xs font-semibold ${kpis.cpi >= 1 ? 'text-green-600' : 'text-red-600'}`}>
                    CPI: {kpis.cpi}
                  </span>
                </div>
                <p className="text-xs text-gray-500">{t('home.budgetSpent')}</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(kpis.budget_spent)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {t('home.remaining')}: {formatCurrency(kpis.budget_remaining)}
                </p>
              </CardContent>
            </Card>

            {/* SPI Card */}
            <Card data-testid="kpi-spi">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <Clock size={18} className="text-purple-600" />
                  {kpis.spi >= 1 ? (
                    <CheckCircle size={16} className="text-green-600" />
                  ) : (
                    <AlertTriangle size={16} className="text-amber-600" />
                  )}
                </div>
                <p className="text-xs text-gray-500">{t('home.schedulePerformance')}</p>
                <p className={`text-xl font-bold ${kpis.spi >= 1 ? 'text-green-600' : kpis.spi >= 0.9 ? 'text-amber-600' : 'text-red-600'}`}>
                  {kpis.spi}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {kpis.spi >= 1 ? t('home.onSchedule') : kpis.spi >= 0.9 ? t('home.slightDelay') : t('home.behindSchedule')}
                </p>
              </CardContent>
            </Card>

            {/* Forecast Card */}
            <Card data-testid="kpi-forecast">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <Target size={18} className="text-red-600" />
                  <TrendingUp size={16} className="text-gray-400" />
                </div>
                <p className="text-xs text-gray-500">{t('home.forecastCompletion')}</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(kpis.forecast_at_completion)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {t('home.vsBudget')}: {formatCurrency(kpis.contract_value)}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Navigation */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t('home.dashboardModules')}</h3>
          
          {/* AI Assistant - Featured */}
          <div 
            className="flex items-center gap-3 bg-gradient-to-r from-indigo-600 to-purple-600 p-3 rounded-lg cursor-pointer hover:from-indigo-700 hover:to-purple-700 transition-all shadow-md"
            onClick={() => navigate('/ai-assistant')}
            data-testid="nav-ai-assistant"
          >
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <Brain size={20} className="text-white" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-white">{t('home.aiAssistant')}</p>
              <p className="text-xs text-white/70">{t('home.aiAssistantDesc')}</p>
            </div>
            <ArrowRight size={18} className="text-white/70" />
          </div>
          
          <div 
            className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-blue-300 transition-colors"
            onClick={() => navigate('/progress')}
            data-testid="nav-progress"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <BarChart3 size={20} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('home.physicalProgressModule')}</p>
              <p className="text-xs text-gray-500">{t('home.physicalProgressDesc')}</p>
            </div>
            <ArrowRight size={18} className="text-gray-400" />
          </div>

          <div 
            className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-green-300 transition-colors"
            onClick={() => navigate('/costs')}
            data-testid="nav-costs"
          >
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <PieChart size={20} className="text-green-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('home.costAnalysis')}</p>
              <p className="text-xs text-gray-500">{t('home.costAnalysisDesc')}</p>
            </div>
            <ArrowRight size={18} className="text-gray-400" />
          </div>

          <div 
            className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-purple-300 transition-colors"
            onClick={() => navigate('/cash')}
            data-testid="nav-cash"
          >
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <DollarSign size={20} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('home.cashFlow')}</p>
              <p className="text-xs text-gray-500">{t('home.cashFlowDesc')}</p>
            </div>
            <ArrowRight size={18} className="text-gray-400" />
          </div>

          <div 
            className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-amber-300 transition-colors"
            onClick={() => navigate('/manpower')}
            data-testid="nav-manpower"
          >
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Users size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('home.manpowerModule')}</p>
              <p className="text-xs text-gray-500">{t('home.manpowerDesc')}</p>
            </div>
            <ArrowRight size={18} className="text-gray-400" />
          </div>

          <div 
            className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-red-300 transition-colors"
            onClick={() => navigate('/deviations')}
            data-testid="nav-deviations"
          >
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('home.deviations')}</p>
              <p className="text-xs text-gray-500">{t('home.deviationsDesc')}</p>
            </div>
            <ArrowRight size={18} className="text-gray-400" />
          </div>

          <div 
            className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-cyan-300 transition-colors"
            onClick={() => navigate('/s-curve')}
            data-testid="nav-scurve"
          >
            <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
              <LineChart size={20} className="text-cyan-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('home.sCurve')}</p>
              <p className="text-xs text-gray-500">{t('home.sCurveDesc')}</p>
            </div>
            <ArrowRight size={18} className="text-gray-400" />
          </div>

          <div 
            className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 cursor-pointer hover:border-violet-300 transition-colors"
            onClick={() => navigate('/evm')}
            data-testid="nav-evm"
          >
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Target size={20} className="text-violet-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{t('home.evmDashboard')}</p>
              <p className="text-xs text-gray-500">{t('home.evmDashboardDesc')}</p>
            </div>
            <ArrowRight size={18} className="text-gray-400" />
          </div>
        </div>

        {/* Deviations Preview */}
        {projectData?.deviations && projectData.deviations.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700">{t('home.activeDeviations')}</h3>
              <span className="text-xs text-red-600 font-medium">{projectData.deviations.length} {t('common.items')}</span>
            </div>
            
            {projectData.deviations.slice(0, 2).map((deviation, index) => (
              <Card key={index} className={`border-l-4 ${getSeverityColor(deviation.severity)}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-700">{deviation.area}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      deviation.severity === 'high' ? 'bg-red-100 text-red-700' :
                      deviation.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {deviation.severity.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">{deviation.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Milestones Preview */}
        {projectData?.milestones && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-700">{t('home.keyMilestones')}</h3>
            <Card>
              <CardContent className="p-3">
                <div className="space-y-2">
                  {projectData.milestones.slice(0, 4).map((milestone, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {milestone.status === 'completed' && <CheckCircle size={14} className="text-green-600" />}
                      {milestone.status === 'in_progress' && <Clock size={14} className="text-blue-600" />}
                      {milestone.status === 'upcoming' && <AlertCircle size={14} className="text-gray-400" />}
                      <span className={`text-xs flex-1 ${milestone.status === 'completed' ? 'text-gray-500' : 'text-gray-700'}`}>
                        {milestone.name}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(milestone.planned).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: '2-digit'})}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostControlHome;
